import { EditableImage } from "@/components/admin/EditableImage";
import { EditableGallery } from "@/components/admin/EditableGallery";
import { EditableText } from "@/components/admin/EditableText";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Podcast } from "lucide-react";

interface ProductHeroSectionProps {
  product: any;
  selectedImage: string;
  isEditMode: boolean;
  onImageSelect: (url: string) => void;
  onUpdateField: (field: string, value: any) => Promise<void>;
}

export function ProductHeroSection({
  product,
  selectedImage,
  isEditMode,
  onImageSelect,
  onUpdateField,
}: ProductHeroSectionProps) {
  const getMediaType = (url: string): 'image' | 'video' | 'pdf' => {
    const getYoutubeVideoId = (url: string): string | null => {
      const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
        /^([a-zA-Z0-9_-]{11})$/
      ];
      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) return match[1];
      }
      return null;
    };
    
    if (url.includes('youtube.com') || url.includes('youtu.be') || getYoutubeVideoId(url)) {
      return 'video';
    }
    if (url.toLowerCase().endsWith('.pdf')) {
      return 'pdf';
    }
    return 'image';
  };

  const renderMedia = () => {
    const mediaType = getMediaType(selectedImage);
    
    if (mediaType === 'video') {
      const getYoutubeVideoId = (url: string): string | null => {
        const patterns = [
          /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
          /^([a-zA-Z0-9_-]{11})$/
        ];
        for (const pattern of patterns) {
          const match = url.match(pattern);
          if (match) return match[1];
        }
        return null;
      };
      const videoId = getYoutubeVideoId(selectedImage);
      return (
        <div className="w-full md:w-[548px] md:max-w-full aspect-video">
          <iframe
            className="w-full h-full rounded-lg"
            src={`https://www.youtube.com/embed/${videoId}`}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      );
    }
    
    if (mediaType === 'pdf') {
      return (
        <div className="w-full md:w-[548px] md:max-w-full aspect-video bg-card rounded-lg overflow-hidden">
          <iframe className="w-full h-full" src={selectedImage} title="PDF viewer" />
        </div>
      );
    }
    
    return (
      <EditableImage
        src={selectedImage}
        alt={product.title}
        onSave={url => onUpdateField('image', url)}
        className="w-full md:w-[548px] md:max-w-full rounded-lg"
      />
    );
  };

  return (
    <div className="space-y-4">
      {/* Profile Image Editor - Admin Only */}
      {isEditMode && (
        <div className="mb-4 p-4 bg-muted/50 rounded-lg border border-dashed border-primary/30">
          <h3 className="text-sm font-semibold text-muted-foreground mb-2">
            Profile Image (Sticky Header Avatar)
          </h3>
          <div className="flex items-center gap-4">
            <EditableImage
              src={product.profile_image || product.image || "https://cdn3.iconfinder.com/data/icons/communication-social-media-1/24/account_profile_user_contact_person_avatar_placeholder-512.png"}
              alt={`${product.title} profile`}
              onSave={async url => {
                await onUpdateField('profile_image', url);
              }}
              className="w-20 h-20 rounded-full object-cover"
              containerClassName="relative"
            />
            <p className="text-xs text-muted-foreground">
              This circular image appears in the sticky header bar
            </p>
          </div>
        </div>
      )}
      
      {/* Main Showcase - Apple Music-style dynamic background */}
      <div className="relative rounded-2xl overflow-hidden min-h-[280px] md:min-h-[320px]">
        {/* Dynamic blurred background layer - pulls color from the image (hidden on mobile) */}
        <div 
          className="hidden md:block absolute inset-0 scale-150 blur-3xl opacity-70"
          style={{ 
            backgroundImage: `url(${selectedImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        
        {/* Secondary glow layer for extra depth (hidden on mobile) */}
        <div 
          className="hidden md:block absolute inset-0 scale-125 blur-2xl opacity-40"
          style={{ 
            backgroundImage: `url(${selectedImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        
        {/* Dark gradient overlay for contrast and readability (hidden on mobile) */}
        <div className="hidden md:block absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40" />
        
        {/* Subtle vignette effect (hidden on mobile) */}
        <div className="hidden md:block absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,0,0,0.3)_100%)]" />
        
        {/* Simple mobile background */}
        <div className="md:hidden absolute inset-0 bg-muted/30" />
        
        {/* Main content with lifted/floating image */}
        <div className="relative p-4 md:p-6 flex items-center justify-center min-h-[280px] md:min-h-[320px]">
          <div className="relative">
            {/* Soft shadow underneath for floating effect */}
            <div 
              className="absolute inset-0 translate-y-4 blur-2xl opacity-50 rounded-xl"
              style={{ 
                backgroundImage: `url(${selectedImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
            {/* Main image with lifted styling */}
            <div className="relative rounded-xl overflow-hidden shadow-[0_25px_60px_-12px_rgba(0,0,0,0.5)] ring-1 ring-white/10">
              {renderMedia()}
            </div>
          </div>
        </div>
      </div>

      {/* Media Thumbnails */}
      {(product.gallery && product.gallery.length > 0 || isEditMode) && (
        <EditableGallery
          images={product.gallery || []}
          selectedImage={selectedImage}
          onImageSelect={onImageSelect}
          onSave={images => onUpdateField('gallery', images)}
        />
      )}

      {/* Circle's Take - Quick Podcast */}
      {product.podcast_audio_url && (
        <div className="mt-6">
          <Card className="p-3 sm:p-4 md:p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-2 border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-2 sm:gap-3 md:gap-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-blue-600 flex items-center justify-center">
                  <Podcast className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                </div>
              </div>
              <div className="flex-1 space-y-2 md:space-y-3">
                <div>
                  <h3 className="text-sm sm:text-base md:text-lg font-bold text-foreground flex items-center gap-2 flex-wrap">
                    {isEditMode ? (
                      <EditableText
                        value={product.podcast_title || "Circle's Take: Quick Listen"}
                        onSave={value => onUpdateField('podcast_title', value)}
                        className="inline"
                      />
                    ) : (
                      <span>{product.podcast_title || "Circle's Take: Quick Listen"}</span>
                    )}
                    <Badge variant="secondary" className="text-xs">Under 5 min</Badge>
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                    Perfect for your drive between showings—get the key insights now
                  </p>
                </div>
                {isEditMode && (
                  <div className="space-y-2">
                    <label className="text-xs text-muted-foreground">Audio URL:</label>
                    <EditableText
                      value={product.podcast_audio_url}
                      onSave={value => onUpdateField('podcast_audio_url', value)}
                      className="text-sm font-mono"
                    />
                  </div>
                )}
                <audio controls className="w-full h-8 sm:h-10" preload="metadata">
                  <source src={product.podcast_audio_url} type="audio/mpeg" />
                  Your browser does not support the audio element.
                </audio>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
